import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, StyledEngineProvider, createTheme, adaptV4Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { Toolbar, CssBaseline } from '@mui/material';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import LoginSignupContext from '../../Reusables/VisualAndAccord/Context/LoginSignupContext';
import { initGA4, trackPageView } from '../../Utils/ga4Tracking';

// Import your components
import Navbar from '../NewUI/MainComponents/Navbar';
import CookieBanner from '../NewUI/MainComponents/CookieBanner';
import Footer from '../NewUI/MainComponents/Footer';
import Home from '../NewUI/Home';
import RetirementSolutions from '../NewUI/RetirementSolutions';
import UnclaimedMoney from '../NewUI/UnclaimedMoney';
import RetirementCalculator from '../NewUI/RetirementCalculator';
import FinancialAdvisorsPage from '../NewUI/FinancialAdvisors';
import Pricing from '../NewUI/Pricing';
import BlogPage from '../NewUI/Blogs';
import Philosophy from '../NewUI/Philosophy';
import Security from '../NewUI/Security';
import FAQ from '../NewUI/FAQs';
import Privacy from '../NewUI/Privacy';
import BlogDetails from '../NewUI/Blogs/BlogDetails';
import NewsletterDetails from '../NewUI/Newsletter/NewsletterDetails';
import Algorithm from '../Algorithm/Algorithm';
import Faqs from '../FAQs/Faqs';
import StaticEmployer from '../Employer2/StaticEmployer';
import CalculatorStatic from '../401kCalculatorStatic/CalculatorStatic';
import TermsAndCondition from '../TermsAndCondition/TermsAndCondition';
import Error404 from '../404/Error404';
import AdvisorSearch from '../NewUI/Advisor';
import PartnersPage from '../NewUI/Partners';
import NewsletterPage from '../NewUI/Newsletter';
import PressPage from '../NewUI/Press';
import WhereIsMyRefundPage from '../NewUI/WhereIsMyRefund';
import FindOld401k from '../NewUI/FindOld401k';
import ComingSoon from '../NewUI/ComingSoon';
import ComingSoonComponent from '../NewUI/ComingSoon';
import Challenge21Days from '../Challenges/Challenge21Days';
import About from '../NewUI/About';
import Employer401k from '../NewUI/Employer401k';
import Employer403b from '../NewUI/Employer403b';
// Accounts
import Guide403b from '../ResourceHubPages/Accounts/403bGuide/Guide403b';
import Guide457b from '../ResourceHubPages/Accounts/457bPlanGuide/Guide457b';
import TspThriftSavingPlanGuide from '../ResourceHubPages/Accounts/TspThriftSavingPlanGuide/TspThriftSavingPlanGuide';
import HsaContributionLimits from '../ResourceHubPages/Accounts/HsaContributionLimits/HsaContributionLimits';
import SocialSecurityBenefits from '../ResourceHubPages/Accounts/SocialSecurityBenefits/SocialSecurityBenefits';
import SwitchingJobs401k from '../ResourceHubPages/Accounts/SwitchingJobs401k/SwitchingJobs401k';
import BackdoorRothIra from '../ResourceHubPages/Accounts/BackdoorRothIra/BackdoorRothIra';
import DivorceAndRetirement from '../ResourceHubPages/FamilyDebt/DivorceAndRetirement/DivorceAndRetirement';
import EstatePlanningBasics from '../ResourceHubPages/FamilyDebt/EstatePlanningBasics/EstatePlanningBasics';

// Benchmarks
import Average403bBalance from '../ResourceHubPages/Benchmarks/Average403bBalanceByAge/Average403bBalance';
import AverageIraBalance from '../ResourceHubPages/Benchmarks/AverageIraBalanceByAge/AverageIraBalance';
import AverageNetWorthByAge from '../ResourceHubPages/Benchmarks/AverageNetWorthByAge/AverageNetWorthByAge';
import AverageSavingsByAge from '../ResourceHubPages/Benchmarks/AverageSavingsByAge/AverageSavingsByAge';
import AverageCreditCardDebt from '../ResourceHubPages/Benchmarks/AverageCreditCardDebt/AverageCreditCardDebt';
import AverageStudentLoanDebt from '../ResourceHubPages/Benchmarks/AverageStudentLoanDebt/AverageStudentLoanDebt';
import HowMuch401kByAge from '../ResourceHubPages/Benchmarks/401kByAge/HowMuch401kByAge';

// StateHub
import AverageMonthlyExpensesByState from '../ResourceHubPages/StateHub/AverageMonthlyExpensesByState/AverageMonthlyExpensesByState';
import AverageSalaryByState from '../ResourceHubPages/IncomeCosts/AverageSalaryByState/AverageSalaryByState';
import BestStatesToRetire from '../ResourceHubPages/StateHub/BestStatesToRetire/BestStatesToRetire';
import CheapestStateToRetireFrom from '../ResourceHubPages/StateHub/CheapestStateToRetireFrom/CheapestStateToRetireFrom';
import TaxFriendlyStatesForRetirees from '../ResourceHubPages/StateHub/TaxFriendlyStatesForRetirees/TaxFriendlyStatesForRetirees';
import BestStatesForEarlyRetirement from '../ResourceHubPages/StateHub/BestStatesForEarlyRetirement/BestStatesForEarlyRetirement';
import ToolStateComparison from '../ResourceHubPages/StateHub/ToolStateComarison/ToolStateComparison';

// Healthcare
import HealthCareCostInRetirement from '../ResourceHubPages/Healthcare/HealthCareCostInRetirement/HealthCareCostInRetirement';
import HealthInsuranceCost from '../ResourceHubPages/Healthcare/HealthInsuranceCost/HealthInsuranceCost';
import LongTermCareCost from '../ResourceHubPages/Healthcare/LongTermCareCost/LongTermCareCost';
import MedicareGuide from '../ResourceHubPages/Healthcare/MedicareGuide/MedicareGuide';
import MedicareSuplementMedigapComparison from '../ResourceHubPages/Calculators/MedicareSuplementMedigapComparison/MedicareSuplementMedigapComparison';

// Planning
import HowMuchRetire from '../ResourceHubPages/Planning/HowMuchRetire/HowMuchRetire';
import HowToPlanRetirement from '../ResourceHubPages/Planning/HowToPlanRetirement/HowToPlanRetirement';
import RetirementStatistics from '../ResourceHubPages/Planning/RetirementStatistics/RetirementStatistics';
import RetireEarly from '../ResourceHubPages/Planning/RetireEarly/RetireEarly';
import RetirementSavingGapGenderRace from '../ResourceHubPages/Planning/RetirementSavingGapGenderRace/RetirementSavingGapGenderRace';
import RothVsTraditional from '../ResourceHubPages/Planning/RothVsTraditional/RothVsTraditional';
import PlootusVsBestRetirementTools from '../ResourceHubPages/Planning/PlootusVsBestRetirementTools/PlootusVsBestRetirementTools';

// Family & Debt
import CostOfRaisiingChildByState from '../ResourceHubPages/FamilyDebt/CostOfRaisiingChildByState/CostOfRaisiingChildByState';
import ChildCareCost from '../ResourceHubPages/FamilyDebt/ChildCareCost/ChildCareCost';

// Income & Costs
import MedianHouseholdIncome from '../ResourceHubPages/IncomeCosts/MedianHouseHoldIncome/MedianHouseholdIncome';
import MinimumWageByState from '../ResourceHubPages/IncomeCosts/MinimumWageByState/MinimumWageByState';
import RentByCity from '../ResourceHubPages/IncomeCosts/RentByCity/RentByCity';

// Calculators
import CompoundInterestCalculator from '../ResourceHubPages/Calculators/CompoundInterestCalculator/CompoundInterestCalculator';
import InflationRetirementCalculator from '../ResourceHubPages/Calculators/InflationRetirementCalculator/InflationRetirementCalculator';
import RetirementIncomeCalculator from '../ResourceHubPages/Calculators/RetirementIncomeCalculator/RetirementIncomeCalculator';
import RmdCalculator from '../ResourceHubPages/Calculators/RmdCalculator/RmdCalculator';
import SocialSecurityCalculator from '../ResourceHubPages/Calculators/SocialSecurityCalculator/SocialSecurityCalculator';
import PlootusHub from '../ResourceHubPages/PlootusHub/PlootusHub';
import FederalIncomeTaxBrackets from '../ResourceHubPages/Tax/FederalIncomeTaxBrackets/FederalIncomeTaxBrackets';
import StandardDeduction2026 from '../ResourceHubPages/Tax/StandardDeduction2026/StandardDeduction2026';
import CapitalGainsTaxRates from '../ResourceHubPages/Tax/CapitalGainsTaxRates/CapitalGainsTaxRates';
import IraContributionLimits from '../ResourceHubPages/Tax/IraContributionLimits/IraContributionLimits';
import TaxLossHarvesting from '../ResourceHubPages/Tax/TaxLossHarvesting/TaxLossHarvesting';
import RothConversionStrategy from '../ResourceHubPages/Tax/RothConversionStrategy/RothConversionStrategy';
import SocialSecurityTaxCalculator from '../ResourceHubPages/Tax/SocialSecurityTaxCalculator/SocialSecurityTaxCalculator';
import RetirementTaxGuide from '../ResourceHubPages/Tax/RetirementTaxGuide/RetirementTaxGuide';
import RetirementPlanningInYour30s from '../ResourceHubPages/ByLifeStage/RetirementPlanningInYour30s/RetirementPlanningInYour30s';
import RetirementPlanningInYour40s from '../ResourceHubPages/ByLifeStage/RetirementPlanningInYour40s/RetirementPlanningInYour40s';
import RetirementPlanningInYour50s from '../ResourceHubPages/ByLifeStage/RetirementPlanningInYour50s/RetirementPlanningInYour50s';
// Create theme instance
const theme = createTheme(adaptV4Theme({
  palette: {
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
  },
}));

const useStyles = makeStyles(() => ({
  '@global': {
    'html, body': {
      backgroundColor: '#FFFFFF !important',
      margin: 0,
      padding: 0,
    },
    '#root': {
      backgroundColor: '#FFFFFF !important',
    },
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#FFFFFF !important',
    width: '100%',
    position: 'relative',
  },
  toolbar: {
    minHeight: '64px !important',
    backgroundColor: '#FFFFFF',
    '@media (min-width: 960px)': {
      minHeight: '80px !important',
    },
  },
  mainContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1,
    backgroundColor: '#FFFFFF !important',
    width: '100%',
  },
}));

const StaticPages = () => {
  const classes = useStyles();
  const location = useLocation();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const [signupModal, setSignupModal] = useState(false);
  const [loginModal, setLoginModal] = useState(
    queryParams.get('viaEmail') || false
  );
  const [idx, setIdx] = useState(0);

  // Handle session expiration by opening login modal
  const userReducer = useSelector((state) => state.userReducer);
  const dispatch = useDispatch();

  useEffect(() => {
    if (userReducer.session_expired) {
      setLoginModal(true);
      setIdx(1); // Default to user login
      dispatch({
        type: 'GENERAL_USER_REDUCER',
        payload: { session_expired: false },
      });
    }
  }, [userReducer.session_expired, dispatch]);

  useEffect(() => {
    if (location.pathname === '/login') {
      setLoginModal(true);
      setIdx(1);
    }
  }, [location.pathname]);

  // Tawk chat integration
  useEffect(() => {
    var Tawk_API = Tawk_API || {},
      Tawk_LoadStart = new Date();
    var s1 = document.createElement('script'),
      s0 = document.getElementsByTagName('script')[0];
    s1.async = true;
    s1.src = 'https://embed.tawk.to/5a57a3a2d7591465c706a90d/default';
    s1.charset = 'UTF-8';
    s1.setAttribute('crossorigin', '*');
    s0.parentNode.insertBefore(s1, s0);
  }, []);

  // Initialize GA4 on mount
  useEffect(() => {
    initGA4();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginSignupContext.Provider
          value={{
            signupModal,
            setSignupModal,
            loginModal,
            setLoginModal,
            idx,
            setIdx,
          }}
        >
          <div className={classes.root}>
            <Navbar />
            {location.pathname === '/21dayfinancialchallenge' ? null : (
              <Toolbar className={classes.toolbar} />
            )}
            <main className={classes.mainContent}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route
                  path="/retirement-solutions"
                  element={<RetirementSolutions />}
                />
                <Route
                  path="/individual-users"
                  element={<RetirementSolutions />}
                />
                <Route
                  path="/financial-advisors"
                  element={<FinancialAdvisorsPage />}
                />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/newsletter" element={<NewsletterPage />} />
                <Route path="/press" element={<PressPage />} />
                <Route path="/where-is-my-refund" element={<WhereIsMyRefundPage />} />
                <Route path="/find-old-401k" element={<FindOld401k />} />
                <Route path="/find-lost-retirement-accounts" element={<FindOld401k />} />
                <Route path="/lost-retirement-accounts" element={<FindOld401k />} />
                <Route path="/unclaimed-retirement-money" element={<FindOld401k />} />
                <Route path="/retirement-savings-lost-and-found" element={<FindOld401k />} />
                <Route path="/unclaimed-money" element={<UnclaimedMoney />} />
                <Route path="/retirement-calculator" element={<RetirementCalculator />} />
                <Route path="/about" element={<About />} />
                <Route path="/solutions" element={<RetirementSolutions />} />
                <Route path="/ai-retirement-calculator" element={<RetirementCalculator />} />
                <Route path="/401k-retirement-calculator" element={<RetirementCalculator />} />
                <Route path="/403b-retirement-calculator" element={<RetirementCalculator />} />
                <Route path="/457b-retirement-calculator" element={<RetirementCalculator />} />
                <Route path="/free-retirement-calculator" element={<RetirementCalculator />} />
                <Route path="/21dayfinancialchallenge" element={<Challenge21Days />} />
                <Route path="/blogs" element={<BlogPage />} />
                <Route path="/finance-101" element={<BlogPage />} />
                <Route path="/blogs/:slug" element={<BlogDetails />} />
                <Route path="/newsletters/:slug" element={<NewsletterDetails />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/philosophy" element={<Philosophy />} />
                <Route path="/algorithm" element={<Algorithm />} />
                <Route path="/security" element={<Security />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/faqs" element={<FAQ />} />
                <Route path="/401k/:employerName" element={<Employer401k />} />
                <Route path="/:employerName" element={<Employer403b />} />
                <Route path="/employer" element={<StaticEmployer />} />
                <Route path="/401kCalculator" element={<CalculatorStatic />} />
                <Route path="/termsandcondition" element={<TermsAndCondition />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/login" element={<Home />} />
                {/* <Route path="/:employerName" element={<Employer401k />} /> */}

                 {/* Routes for resource hub pages */}

                <Route path="/401k-by-age" element={<HowMuch401kByAge />} />
                <Route path="/403b-guide" element={<Guide403b />} />
                <Route path="/457b-plan-guide" element={<Guide457b />} />
                <Route path="/average-403b-balance-by-age" element={<Average403bBalance />} />
                <Route path="/average-ira-balance-by-age" element={<AverageIraBalance />} />
                <Route path="/average-monthly-expenses-by-state" element={<AverageMonthlyExpensesByState />} />
                <Route path="/average-net-worth-by-age" element={<AverageNetWorthByAge />} />
                <Route path="/average-salary-by-state" element={<AverageSalaryByState />} />
                <Route path="/average-credit-card-debt" element={<AverageCreditCardDebt />} />
                <Route path="/best-states-to-retire" element={<BestStatesToRetire />} />
                <Route path="/best-states-for-early-retirement" element={<BestStatesForEarlyRetirement />} />
                <Route path="/cheapest-states-to-retire" element={<CheapestStateToRetireFrom />} />
                <Route path="/compound-interest-calculator" element={<CompoundInterestCalculator />} />
                <Route path="/hsa-contribution-limits" element={<HsaContributionLimits />} />
                <Route path="/inflation-retirement-calculator" element={<InflationRetirementCalculator />} />
                <Route path="/medicare-supplement-medigap-comparison" element={<MedicareSuplementMedigapComparison />} />
                <Route path="/retirement-income-calculator" element={<RetirementIncomeCalculator />} />
                <Route path="/retirement-savings-gap-gender-race" element={<RetirementSavingGapGenderRace />} />
                <Route path="/tsp-thrift-savings-plan-guide" element={<TspThriftSavingPlanGuide />} />
                <Route path="/healthcare-costs-in-retirement" element={<HealthCareCostInRetirement />} />
                <Route path="/how-much-to-retire" element={<HowMuchRetire />} />
                <Route path="/how-to-plan-retirement" element={<HowToPlanRetirement />} />
                <Route path="/retirement-statistics-2026" element={<RetirementStatistics />} />
                <Route path="/social-security-benefits" element={<SocialSecurityBenefits />} />
                <Route path="/retire-early" element={<RetireEarly />} />
                <Route path="/roth-vs-traditional" element={<RothVsTraditional />} />
                <Route path="/tax-friendly-states-for-retirees" element={<TaxFriendlyStatesForRetirees />} />
                <Route path="/plootus-vs-best-retirement-tools" element={<PlootusVsBestRetirementTools />} />
                <Route path="/cost-of-raising-child-by-state" element={<CostOfRaisiingChildByState />} />
                <Route path="/childcare-costs" element={<ChildCareCost />} />
                <Route path="/tool-state-comparison" element={<ToolStateComparison />} />
                <Route path="/health-insurance-costs" element={<HealthInsuranceCost />} />
                <Route path="/long-term-care-costs" element={<LongTermCareCost />} />
                <Route path="/median-household-income" element={<MedianHouseholdIncome />} />
                <Route path="/minimum-wage-by-state" element={<MinimumWageByState />} />
                <Route path='/rent-by-city' element={<RentByCity />} />
                <Route path="/average-savings-by-age" element={<AverageSavingsByAge />} />
                <Route path="/average-student-loan-debt" element={<AverageStudentLoanDebt />} />
                <Route path="/rmd-calculator" element={<RmdCalculator />} />
                <Route path="/social-security-calculator" element={<SocialSecurityCalculator />} />
                <Route path="/federal-income-tax-brackets" element={<FederalIncomeTaxBrackets />} />
                <Route path="/standard-deduction-2026" element={<StandardDeduction2026 />} />
                <Route path="/capital-gains-tax-rates" element={<CapitalGainsTaxRates />} />
                <Route path="/ira-contribution-limits" element={<IraContributionLimits />} />
                <Route path="/tax-loss-harvesting" element={<TaxLossHarvesting />} />
                <Route path="/roth-conversion-strategy" element={<RothConversionStrategy />} />
                <Route path="/social-security-tax-calculator" element={<SocialSecurityTaxCalculator />} />
                <Route path="/retirement-tax-guide" element={<RetirementTaxGuide />} />
                <Route path="/retirement-planning-in-your-30s" element={<RetirementPlanningInYour30s />} />
                <Route path="/retirement-planning-in-your-40s" element={<RetirementPlanningInYour40s />} />
                <Route path="/retirement-planning-in-your-50s" element={<RetirementPlanningInYour50s />} />
                <Route path="/divorce-and-retirement" element={<DivorceAndRetirement />} />
                <Route path="/estate-planning-basics" element={<EstatePlanningBasics />} />
                <Route path="/switching-jobs-401k" element={<SwitchingJobs401k />} />
                <Route path="/backdoor-roth-ira" element={<BackdoorRothIra />} />
                <Route path="/medicare-guide" element={<MedicareGuide />} />
                <Route path="/retirement-guide" element={<PlootusHub />} />
              

                {/* Default route  */}
                <Route path="*" element={<Error404 />} />
              </Routes>
            </main>
            <Footer />
            <CookieBanner />
          </div>
        </LoginSignupContext.Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default StaticPages;
